import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  createRegistrationForm,
  listRegistrationApplications,
  listRegistrationForms,
  reviewRegistrationApplication,
  updateRegistrationForm,
  verifyRegistrationVat,
  type RegistrationApplication,
  type RegistrationForm,
  type RegistrationFormInput,
} from './registrationApi';

function eventValue(event: Event): string {
  return (event.currentTarget as HTMLElement & { value: string }).value;
}

function label(value: string): string {
  return value.replaceAll('_', ' ');
}

export function RegistrationPanel() {
  const [forms, setForms] = useState<RegistrationForm[]>([]);
  const [applications, setApplications] = useState<RegistrationApplication[]>([]);
  const [selectedFormId, setSelectedFormId] = useState('');
  const [name, setName] = useState('Wholesale registration');
  const [title, setTitle] = useState('Apply for a wholesale account');
  const [description, setDescription] = useState('');
  const [formStatus, setFormStatus] = useState<'draft' | 'active' | 'disabled'>('draft');
  const [privacyUrl, setPrivacyUrl] = useState('');
  const [requireVat, setRequireVat] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyApplicationId, setBusyApplicationId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const selectedForm = useMemo(
    () => forms.find((item) => item.id === selectedFormId) ?? null,
    [forms, selectedFormId],
  );

  const applyForm = useCallback((form: RegistrationForm | null) => {
    if (!form) {
      setSelectedFormId('');
      setName('Wholesale registration');
      setTitle('Apply for a wholesale account');
      setDescription('');
      setFormStatus('draft');
      setPrivacyUrl('');
      setRequireVat(false);
      return;
    }
    setSelectedFormId(form.id);
    setName(form.name);
    setTitle(form.title);
    setDescription(form.description ?? '');
    setFormStatus(form.status);
    setPrivacyUrl(form.settings.privacy_notice_url ?? '');
    setRequireVat(form.settings.require_vat);
  }, []);

  const refresh = useCallback(async () => {
    const [formRows, applicationRows] = await Promise.all([
      listRegistrationForms(),
      listRegistrationApplications(),
    ]);
    setForms(formRows);
    setApplications(applicationRows);
    setSelectedFormId((current) => {
      if (current && formRows.some((item) => item.id === current)) return current;
      return formRows[0]?.id ?? '';
    });
    return formRows;
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    refresh()
      .then((formRows) => {
        if (!active) return;
        applyForm(formRows[0] ?? null);
      })
      .catch((reason: unknown) => {
        if (active) {
          setError(reason instanceof Error ? reason.message : 'Could not load B2B registration.');
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [applyForm, refresh]);

  const saveForm = useCallback(async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const input: RegistrationFormInput = {
        name: name.trim(),
        title: title.trim(),
        description: description.trim() || null,
        status: formStatus,
        settings: {
          require_vat: requireVat,
          allowed_countries: selectedForm?.settings.allowed_countries ?? [],
          business_types: selectedForm?.settings.business_types ?? [],
          custom_fields: selectedForm?.settings.custom_fields ?? [],
          privacy_notice_url: privacyUrl.trim() || null,
          privacy_notice_version: selectedForm?.settings.privacy_notice_version ?? '1',
          consent_text:
            selectedForm?.settings.consent_text ??
            'I agree that my information may be used to review this wholesale application.',
        },
      };
      const saved = selectedForm
        ? await updateRegistrationForm(selectedForm.id, input)
        : await createRegistrationForm(input);
      const rows = await refresh();
      const current = rows.find((item) => item.id === saved.id) ?? saved;
      applyForm(current);
      setNotice(
        current.status === 'active'
          ? 'Registration form is active. Public storefront rendering is the next M11 slice.'
          : 'Registration form draft saved.',
      );
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : 'Could not save registration form.');
    } finally {
      setSaving(false);
    }
  }, [
    applyForm,
    description,
    formStatus,
    name,
    privacyUrl,
    refresh,
    requireVat,
    selectedForm,
    title,
  ]);

  const actOnApplication = useCallback(
    async (
      applicationId: string,
      action: 'verify' | 'under_review' | 'needs_information' | 'approved' | 'rejected',
    ) => {
      setBusyApplicationId(applicationId);
      setError(null);
      setNotice(null);
      try {
        const updated =
          action === 'verify'
            ? await verifyRegistrationVat(applicationId)
            : await reviewRegistrationApplication(applicationId, action);
        setApplications((current) =>
          current.map((item) => (item.id === applicationId ? updated : item)),
        );
        if (action === 'verify') {
          setNotice(`VAT verification result: ${label(updated.vat.status)}.`);
        } else if (action === 'approved') {
          setNotice(
            updated.warnings?.[0] ??
              'Application approved. Shopify Company provisioning is intentionally not enabled yet.',
          );
        } else {
          setNotice(`Application moved to ${label(updated.status)}.`);
        }
      } catch (reason: unknown) {
        setError(reason instanceof Error ? reason.message : 'Could not update application.');
      } finally {
        setBusyApplicationId(null);
      }
    },
    [],
  );

  return (
    <s-section heading="B2B Registration & approval">
      <s-stack direction="block" gap="base">
        <s-paragraph>
          Collect wholesale applications, verify business VAT evidence, and approve buyers before
          any Shopify Company is created.
        </s-paragraph>

        {loading && <s-paragraph>Loading registration setup…</s-paragraph>}

        {!loading && forms.length > 0 && (
          <s-select
            label="Registration form"
            value={selectedFormId}
            onChange={(event) => {
              const id = eventValue(event);
              applyForm(forms.find((item) => item.id === id) ?? null);
            }}
          >
            {forms.map((form) => (
              <s-option key={form.id} value={form.id}>
                {form.name} — {label(form.status)}
              </s-option>
            ))}
          </s-select>
        )}

        <s-heading>{selectedForm ? 'Edit registration form' : 'Create registration form'}</s-heading>
        <s-text-field label="Internal name" value={name} onInput={(event) => setName(eventValue(event))} />
        <s-text-field label="Public heading" value={title} onInput={(event) => setTitle(eventValue(event))} />
        <s-text-field
          label="Public description"
          value={description}
          onInput={(event) => setDescription(eventValue(event))}
        />
        <s-text-field
          label="Privacy policy URL"
          value={privacyUrl}
          placeholder="https://example.com/privacy"
          onInput={(event) => setPrivacyUrl(eventValue(event))}
        />
        <s-select
          label="VAT number"
          value={requireVat ? 'required' : 'optional'}
          onChange={(event) => setRequireVat(eventValue(event) === 'required')}
        >
          <s-option value="optional">Optional</s-option>
          <s-option value="required">Required</s-option>
        </s-select>
        <s-select
          label="Form status"
          value={formStatus}
          onChange={(event) => setFormStatus(eventValue(event) as typeof formStatus)}
        >
          <s-option value="draft">Draft</s-option>
          <s-option value="active">Active</s-option>
          <s-option value="disabled">Disabled</s-option>
        </s-select>
        <s-stack direction="inline" gap="small">
          <s-button onClick={() => void saveForm()} disabled={saving || !name.trim() || !title.trim()}>
            {saving ? 'Saving…' : selectedForm ? 'Save registration form' : 'Create registration form'}
          </s-button>
          {selectedForm && (
            <s-button onClick={() => applyForm(null)} disabled={saving}>
              New form
            </s-button>
          )}
        </s-stack>

        {selectedForm && (
          <s-paragraph>
            Public form key: {selectedForm.public_key}. The customer-facing storefront page is not
            exposed until M11.2 adds anti-abuse protection and rendering.
          </s-paragraph>
        )}

        <s-heading>Applications</s-heading>
        {applications.length === 0 && <s-paragraph>No wholesale applications yet.</s-paragraph>}
        {applications.map((application) => {
          const busy = busyApplicationId === application.id;
          return (
            <s-stack key={application.id} direction="block" gap="small">
              <s-heading>{application.company.name}</s-heading>
              <s-paragraph>
                {application.contact.first_name} {application.contact.last_name} ·{' '}
                {application.contact.email} · {application.company.country_code}
              </s-paragraph>
              <s-paragraph>
                Status: {label(application.status)} · VAT: {label(application.vat.status)}
                {application.vat.number
                  ? ` (${application.vat.country_code ?? ''}${application.vat.number})`
                  : ''}
              </s-paragraph>
              {application.vat.business_name && (
                <s-paragraph>
                  Registry match: {application.vat.business_name}
                  {application.vat.reference ? ` · reference ${application.vat.reference}` : ''}
                </s-paragraph>
              )}
              <s-stack direction="inline" gap="small">
                <s-button
                  onClick={() => void actOnApplication(application.id, 'verify')}
                  disabled={busy || !application.vat.number}
                >
                  Verify VAT
                </s-button>
                <s-button
                  onClick={() => void actOnApplication(application.id, 'under_review')}
                  disabled={busy}
                >
                  Review
                </s-button>
                <s-button
                  onClick={() => void actOnApplication(application.id, 'needs_information')}
                  disabled={busy}
                >
                  Need information
                </s-button>
                <s-button
                  onClick={() => void actOnApplication(application.id, 'approved')}
                  disabled={busy || application.status === 'approved'}
                >
                  Approve
                </s-button>
                <s-button
                  onClick={() => void actOnApplication(application.id, 'rejected')}
                  disabled={busy || application.status === 'rejected'}
                >
                  Reject
                </s-button>
              </s-stack>
            </s-stack>
          );
        })}

        {notice && <s-paragraph>{notice}</s-paragraph>}
        {error && <s-paragraph>Registration needs attention: {error}</s-paragraph>}
      </s-stack>
    </s-section>
  );
}
